import { AlertCircle, Mail, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';

const SUPPORT_EMAIL =
  (import.meta.env.VITE_SUPPORT_EMAIL as string | undefined)?.trim() ||
  'support@odonboard.com';

interface FatalErrorScreenProps {
  error: string;
  onSignOut?: () => void;
}

export function FatalErrorScreen({ error, onSignOut }: FatalErrorScreenProps) {
  // Check if error is related to missing company_name
  const isMissingCompanyName =
    error.includes('Company name is required') ||
    error.includes('Company name missing') ||
    error.includes('Invalid company name format') ||
    error.includes('account setup is incomplete');

  // Check if error is related to invited user state
  const isInvitedUserError =
    error.includes('not properly set up') ||
    error.includes('accept your invitation');

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900 p-4">
      <div className="max-w-md w-full">
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl p-8 border border-slate-200 dark:border-slate-700">
          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className="rounded-full bg-red-50 dark:bg-red-900/20 p-4">
              <AlertCircle className="h-12 w-12 text-red-600 dark:text-red-400" />
            </div>
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-center text-slate-900 dark:text-white mb-3">
            {isMissingCompanyName && 'Account Setup Incomplete'}
            {isInvitedUserError && 'Invalid Account State'}
            {!isMissingCompanyName && !isInvitedUserError && 'Unable to Initialize Account'}
          </h1>

          {/* Main Message */}
          <div className="text-center mb-6">
            <p className="text-slate-600 dark:text-slate-300 text-base mb-4">
              {isMissingCompanyName && (
                <>Your account setup is incomplete. Please contact support or re-sign up.</>
              )}
              {isInvitedUserError && (
                <>Your account is not properly set up. Please accept your invitation link again or contact your administrator.</>
              )}
              {!isMissingCompanyName && !isInvitedUserError && (
                <>We encountered an issue setting up your account. Please try again or contact support.</>
              )}
            </p>

            {/* Technical Details (collapsed) */}
            <details className="text-left">
              <summary className="text-sm text-slate-500 dark:text-slate-400 cursor-pointer hover:text-slate-700 dark:hover:text-slate-200">
                Technical Details
              </summary>
              <div className="mt-2 p-3 bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700">
                <code className="text-xs text-slate-600 dark:text-slate-300 break-all">
                  {error}
                </code>
              </div>
            </details>
          </div>

          {/* Actions */}
          <div className="space-y-3">
            {/* Contact Support Button */}
            <Button asChild className="w-full" variant="default">
              <a href={`mailto:${SUPPORT_EMAIL}?subject=Account+Setup+Issue`}>
                <Mail className="h-4 w-4 mr-2" />
                Contact Support
              </a>
            </Button>

            {/* Sign Out Button */}
            {onSignOut && (
              <Button
                onClick={onSignOut}
                className="w-full"
                variant="outline"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Sign Out and Try Again
              </Button>
            )}
          </div>

          {/* Help Text */}
          <div className="mt-6 pt-6 border-t border-slate-200 dark:border-slate-700">
            <p className="text-xs text-slate-500 dark:text-slate-400 text-center">
              {isMissingCompanyName && (
                <>
                  This usually happens when required information is missing from your account.
                  Please sign out and create a new account with all required details.
                </>
              )}
              {isInvitedUserError && (
                <>
                  Please make sure you're using the correct invitation link sent to your email.
                  If the problem persists, ask your administrator to resend the invitation.
                </>
              )}
              {!isMissingCompanyName && !isInvitedUserError && (
                <>
                  If this issue persists, please contact our support team with the technical details above.
                </>
              )}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Need immediate help?{' '}
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="text-brand dark:text-brand hover:underline"
            >
              Email us
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
